import { faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Typography } from "@mui/material"
import { IAgencyFormData } from "agency/model"
import { validateAgencyFormData } from "agency/tools/form"
import { ALL_LOCALES, LOCALES } from "app/language/model"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { isEmptyObject } from "app/tools/common"
import { ALL_CURRENCIES } from "currency/model"
import { getAvailableCurrencyIds } from "currency/tools/common"
import { FieldSelect } from "form/components/fields/FieldSelect"
import { FieldText } from "form/components/fields/FieldText"
import { Form, Formik } from "formik"
import React, { useEffect } from "react"
import { CountrySelect, GetCountries, GetState, StateSelect } from "react-country-state-city"
import { Country, State } from "react-country-state-city/dist/esm/types"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { addImportant } from "ui/tools/common"
import { COUNTRIES_WITCH_REQUIRE_STATE, ISignUpAgencyForm } from "user/model"
import { decrementSignUpStep, incrementSignUpStep, setSignUpAgencyForm } from "user/tools/signup"

interface ISignUpWizardAgencyFormProps {
  agencyForm: ISignUpAgencyForm
  containerWidth: number
}

const SignUpWizardAgencyFormFn = React.memo(({ agencyForm, containerWidth }: ISignUpWizardAgencyFormProps) => {
  const [agencyData, setAgencyData] = React.useState<IAgencyFormData>(agencyForm.data)
  const [country, setCountry] = React.useState<Country>(undefined as any)
  const [state, setState] = React.useState<State>(undefined as any)
  useEffect(() => {
    if (!agencyData.country) {
      return
    }
    GetCountries().then(countries => {
      const c = countries.find(c => c.iso2 === agencyData.country)
      if (!c) {
        return
      }
      setCountry(c)
      if (!agencyData.state) {
        return
      }
      GetState(c.id).then(states => {
        const s = states.find(st => st.name === agencyData.state)
        if (!s) {
          return
        }
        setState(s)
      })
    })
  }, [])
  return (
    <FlexBox fullWidth vertical gap sx={{ maxWidth: "100%", height: "100%", minHeight: "100%", maxHeight: "100%" }}>
      <Formik
        initialValues={agencyData}
        validate={(values: IAgencyFormData) => {
          setAgencyData(values)
          const errors = validateAgencyFormData(values)
          setSignUpAgencyForm({
            data: values,
            errors,
            isValid: isEmptyObject(errors)
          })
          return errors
        }}
        onSubmit={async (_values, actions) => {
          incrementSignUpStep()
          actions.setSubmitting(false)
        }}>
        {({ errors, isSubmitting, values, setFieldValue, touched, setTouched }) => {
          const canSubmit = !isSubmitting && agencyForm.isValid
          return (
            <Form
              id={"signup-agency-form"}
              style={{
                width: "100%",
                padding: ".5rem",
                maxWidth: "100%",
                height: "100%",
                minHeight: "100%",
                maxHeight: "100%"
              }}>
              <FlexBox
                vertical
                gap
                fullWidth
                ai="flex-start"
                jc="flex-start"
                sx={{
                  "overflow": "auto",
                  "maxWidth": "100%",
                  "height": "calc(100% - 5rem)",
                  "minHeight": "calc(100% - 5rem)",
                  "maxHeight": "calc(100% - 5rem)",
                  "pt": ".5rem",
                  "& > *": {
                    width: addImportant(containerWidth > 1030 ? "25rem" : "100%")
                  }
                }}>
                <FieldText
                  fieldName="name"
                  label={translate("user.signup.agency.name")}
                  value={values.name}
                  error={errors.name}
                  autoFocus
                  required
                />
                <CountrySelect
                  id="country-select-field"
                  onChange={(c: any) => {
                    setCountry(c)
                    setFieldValue("country", c.iso2)
                  }}
                  onTextChange={e => {
                    if (e.target.value === "") {
                      setCountry(undefined as any)
                      setFieldValue("country", "")
                    }
                  }}
                  onBlur={() => setTouched({ country: true })}
                  defaultValue={country as any}
                  required
                  containerClassName={`react-country-select ${errors["country"] && touched["country"] ? "error" : ""}`}
                  placeHolder={`${translate("label.country")} *`}
                />
                {errors["country"] && touched["country"] && (
                  <Typography color="error" fontSize=".75rem" sx={{ mt: "-.8rem", ml: "1rem" }}>
                    {errors["country"]}
                  </Typography>
                )}
                {country && COUNTRIES_WITCH_REQUIRE_STATE.includes(country.iso2) && (
                  <>
                    <StateSelect
                      id="state-select-field"
                      countryid={country ? country.id : -1}
                      onChange={(s: any) => setFieldValue("state", s.name)}
                      onBlur={() => setTouched({ state: true })}
                      defaultValue={state as any}
                      required
                      containerClassName={`react-country-select ${errors["state"] && touched["state"] ? "error" : ""}`}
                      placeHolder={`${translate(country.iso2 === "US" ? "label.state" : "label.province")} *`}
                    />
                    {errors["state"] && touched["state"] && (
                      <Typography color="error" fontSize=".75rem" sx={{ mt: "-.8rem", ml: "1rem" }}>
                        {errors["state"]}
                      </Typography>
                    )}
                  </>
                )}
                <FieldSelect
                  fieldName="language_id"
                  label={translate("label.language")}
                  value={values.language_id || LOCALES.EN}
                  options={ALL_LOCALES.slice()}
                  optionLabelFn={locale => translate(`label.language.${locale}`)}
                  error={errors.language_id}
                />
                <FieldSelect
                  fieldName="currency_id"
                  label={translate("label.currency")}
                  value={values.currency_id || ALL_CURRENCIES[0]}
                  options={getAvailableCurrencyIds()}
                  optionLabelFn={(currency: string) => currency}
                  error={errors.currency_id}
                />
              </FlexBox>
              <FlexBox gap fullWidth ai="end" jc="space-between" sx={{ mt: "2rem", width: "100%" }}>
                <Button
                  id="signup-user-form-button-cancel"
                  variant="outlined"
                  onClick={() => decrementSignUpStep()}
                  startIcon={<FontAwesomeIcon icon={faChevronLeft} className="icon-previous" />}>
                  {translate("label.step.previous")}
                </Button>
                <Button
                  id="signup-user-form-button-save"
                  variant="contained"
                  endIcon={<FontAwesomeIcon icon={faChevronRight} className="icon-next" />}
                  type="submit"
                  disabled={!canSubmit}>
                  {translate("label.step.next")}
                </Button>
              </FlexBox>
            </Form>
          )
        }}
      </Formik>
    </FlexBox>
  )
})

export const SignUpWizardAgencyForm = connect(
  (state: IRootState): ISignUpWizardAgencyFormProps => ({
    containerWidth: state.ui.container.width,
    agencyForm: state.user.signup.agencyForm
  })
)(SignUpWizardAgencyFormFn)
